import { useState, useEffect, useCallback } from 'react';
import { Connector } from '@/types';
import { getAllChannelPartners } from '@/services/channelPartnerService';

export interface UseAllChannelPartnersResult {
  partners: Connector[];
  loading: boolean;
  /** Firestore error code (e.g. 'permission-denied') when the read failed */
  errorCode: string | null;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Loads every channel partner in the collection, not just the current user's
 * own/shared ones. Backed by a one-shot read that only admin / business head /
 * operations users are permitted to make (see firestore.rules).
 *
 * Deliberately separate from useChannelPartners(), which stays user-scoped and
 * realtime for the regular partner screens.
 */
export function useAllChannelPartners(): UseAllChannelPartnersResult {
  const [partners, setPartners] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorCode(null);

    try {
      const all = await getAllChannelPartners();
      setPartners(all);
    } catch (err) {
      console.error('Error loading all channel partners:', err);
      const code =
        typeof err === 'object' && err !== null && 'code' in err
          ? String((err as { code: unknown }).code)
          : null;
      setErrorCode(code);
      setError(
        err instanceof Error ? err.message : 'Failed to load channel partners'
      );
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { partners, loading, errorCode, error, refresh: load };
}
